<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            ajax-path="admin/roles"
            @ajaxResponse="(response) => {
                $store.commit('admin/roles/setItems', response);
            }"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit Role</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />                
                <app-select
                    v-model="editForm.permissions"
                    placeholder="Permissions"
                    :error="editForm.errors.get('permissions')"
                    @updated="editForm.errors.clear('permissions')"
                    :options="items.permissions"
                    value-key="id"
                    label-key="name"
                    required
                    multiple
                />                
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Role</template>
            <template slot="body">
                <div>
                    <app-input
                        v-model="addForm.name"
                        placeholder="Name"
                        :error="addForm.errors.get('name')"
                        @updated="addForm.errors.clear('name')"
                        focus
                        required
                    />                    
                    <app-select
                        v-model="addForm.permissions"
                        placeholder="Permissions"
                        :error="addForm.errors.get('permissions')"
                        @updated="addForm.errors.clear('permissions')"
                        :options="items.permissions"
                        value-key="id"
                        label-key="name"
                        required
                        multiple
                    />                    
                    <div class="mt-5 flex items-center justify-end">
                        <app-button 
                            class="btn-white" 
                            @click="showAddModal = false"
                            :disable="addFormIsLoading"
                        >Cancel</app-button>
                        <app-button 
                            class="btn-blue ml-3" 
                            @click="store"
                            :loading="addFormIsLoading"
                        >Add</app-button>
                    </div>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';

    export default {
        data() {
            return {
                addForm: new Form({
                    name: null,
                    permissions: [],
                }),
                editForm: new Form({
                    id: null,
                    name: null,
                    permissions: [],
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                refreshTable: false,
                columns: [
                    {
                        title: 'Name', 
                        field: 'name', 
                        sorter: 'string',
                        minWidth: 150,
                    },                    
                    {
                        title: 'Permissions',
                        field: 'permissions',
                        sorter: 'string',
                        formatter(cell) {
                            if (cell._cell.row.data.name === 'Super Admin') {
                                return 'All Permissions';
                            }

                            return cell._cell.row.data.permissions_for_human;
                        },
                        minWidth: 300,
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: roles => {
                            this.showEditModal = true;
                            this.editForm.set(roles[0]);
                        },
                        requiredSelect: true,
                        isEligible(roles) {
                            return roles[0].name !== 'Super Admin';
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: roles => {
                            this.deleteRole(roles[0]);
                        },
                        requiredSelect: true,
                        isEligible(roles) {
                            return roles[0].name !== 'Super Admin'
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },        
        computed: {
            ...mapGetters({
                items: 'admin/roles/items',
            }),
        },
        methods: {
            edit(role) {
                this.showEditModal = true;
                this.editForm.set(role);
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('admin/roles/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Role was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                if (this.items.permissions.length == 0) {
                    this.$swal.fire({
                        type: 'error',
                        title: 'Oops!',
                        text: 'No permissions found. Please add permission first before adding role.'
                    });

                    return;
                }

                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('admin/roles/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Role was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deleteRole(item) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${item.name}</strong>" role permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/roles/delete', item.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Role was successfully deleted.'
                        })
                    }
                })
                
            },
        }
    }
</script>